import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest, isMsalConfigured } from "./msalConfig";

/* ═══════════════════════════════════════════════════════════════
   AuthGuard — Wraps the portal and enforces Entra ID sign-in.

   - If MSAL is not configured (placeholder values), renders
     children immediately so local dev works without auth.
   - If authenticated, renders children.
   - If not authenticated, shows a branded login page.
   - While handling redirects, shows a loading spinner.
   ═══════════════════════════════════════════════════════════════ */

const T = {
  bg: "#0b1f3a",
  panel: "#112b4d",
  border: "rgba(255,255,255,0.08)",
  text: "#edf4ff",
  muted: "#a8bddc",
  blue: "#44a6ff",
  green: "#53c46d",
};

export default function AuthGuard({ children }) {
  /* Skip auth entirely when MSAL is not configured */
  if (!isMsalConfigured) {
    return children;
  }

  return <AuthGateInner>{children}</AuthGateInner>;
}

/**
 * Inner component — only rendered when MSAL is configured so
 * hooks are always called in the same order.
 */
function AuthGateInner({ children }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  /* ── Redirect in progress (loading) ── */
  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  /* ── Authenticated ── */
  if (isAuthenticated) {
    return children;
  }

  /* ── Not authenticated — show login page ── */
  return <LoginPage onLogin={() => instance.loginRedirect(loginRequest)} />;
}

/* ═══════════════════════════════════════════════════
   Loading screen — shown while handling MSAL redirect
   ═══════════════════════════════════════════════════ */
function LoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, #08182e, ${T.bg})`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans',system-ui,sans-serif",
      color: T.text,
    }}>
      {/* Spinner */}
      <style>{`
        @keyframes authSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        width: 40,
        height: 40,
        border: `3px solid ${T.border}`,
        borderTop: `3px solid ${T.blue}`,
        borderRadius: "50%",
        animation: "authSpin 0.8s linear infinite",
        marginBottom: 20,
      }} />
      <p style={{ fontSize: 14, color: T.muted }}>Signing you in...</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Login page — branded sign-in screen
   ═══════════════════════════════════════════════════ */
function LoginPage({ onLogin }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, #08182e, ${T.bg})`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans',system-ui,sans-serif",
      color: T.text,
    }}>
      {/* Logo */}
      <img
        src="/patriot-logo.png"
        alt="Patriot Consulting"
        style={{ height: 72, width: "auto", marginBottom: 24 }}
      />

      {/* Card */}
      <div style={{
        background: `linear-gradient(180deg, ${T.panel}, #0e2746)`,
        border: `1px solid ${T.border}`,
        borderRadius: 18,
        padding: "36px 40px",
        maxWidth: 400,
        width: "90%",
        textAlign: "center",
        boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
      }}>
        <h1 style={{
          fontSize: 22,
          fontWeight: 800,
          margin: "0 0 6px",
          letterSpacing: "-0.3px",
        }}>
          Client Portal
        </h1>
        <p style={{
          fontSize: 14,
          color: T.muted,
          margin: "0 0 28px",
          lineHeight: 1.5,
        }}>
          Sign in with your company account to access your security dashboard, reports, and services.
        </p>

        {/* Sign in button */}
        <button
          onClick={onLogin}
          style={{
            width: "100%",
            padding: "14px 20px",
            borderRadius: 10,
            border: "1px solid rgba(68,166,255,0.4)",
            background: "rgba(68,166,255,0.15)",
            color: T.blue,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 700,
            transition: "background 0.15s, border-color 0.15s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {/* Microsoft icon */}
          <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
            <rect x="0" y="0" width="10" height="10" fill="#f25022" />
            <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
            <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
            <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
          </svg>
          Sign in with your company account
        </button>

        <p style={{
          fontSize: 11,
          color: T.muted,
          margin: "20px 0 0",
          opacity: 0.6,
          lineHeight: 1.4,
        }}>
          Protected by Microsoft Entra ID
        </p>
      </div>

      {/* Footer */}
      <p style={{
        fontSize: 11,
        color: T.muted,
        marginTop: 32,
        opacity: 0.5,
      }}>
        Patriot Consulting &mdash; Managed Security Services
      </p>
    </div>
  );
}
